import { getBookingDisplayClock, getBookingTimezone, rememberCheckoutDisplay } from '../../../../../platform/util/bookingCalendarLabel';
import { CREATE_APPOINTMENT_CHECKOUT } from "../../../../../platform/graphql/stripePayments";
import { getBookingDisplayCalendar } from '../../../../../platform/util/bookingCalendarLabel';
import React, { useContext, useEffect, useMemo, useState, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import {
  Box,
  Card,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Dialog,
  DialogContent,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import { parse } from "date-fns";
import { useMutation, useQuery } from "@apollo/client";
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";
import {
  GET_BUSINESS_BOOKING_TYPES,
  GET_BOOKINGS_BY_EMAIL,
  GET_INVITEE_ANSWERS,
  GET_INVITEE_QUESTIONS,
  GET_STAFF_BY_URL,
} from "../../../../../platform/graphql/queries";
import { ADD_BOOKINGS } from "../../../../../platform/graphql/mutations";
import Error from "../../../../../platform/components/common/error";
import BookingHeader, { getFormattedTimeRange } from "./bookingHeader";
import BookingForm from "./bookingForm";
import BookingEmailVerification from '../../../../../platform/components/auth/BookingEmailVerification';
import PaymentSection from "./paymentSection";
import { BookingTypeStatus, validateEmail } from "../../../../../platform/util/utils";
import { UserContext } from "../../../../../platform/components/auth/auth-context";
import { getBrowserTimeZone, resolveTimeZone } from "../../../../../platform/util/timezoneUtils";


export function navigateToBookingConfirmation({
  time,
  bookingTypedata,
  storedTimeZone,
  staffData,
  email,
  name,
  businessUrl,
  staffUrl,
  navigate,
  res,
  providerUser,
  entryPath,
  referralLeadId,
  bookedForName,
  bookedForEmail,
  additionalConfirmationEmails,
  sendCopyToBookedBy = true,
}) {
  const meetingTitle = bookingTypedata.title;
  const timeRange = getFormattedTimeRange(time, bookingTypedata.length);

  const fallbackEntryPath = `/${businessUrl}/${bookingTypedata?.link || ""}`;
  const resolvedEntryPath = entryPath || fallbackEntryPath;
  const successEntryPath = (() => {
    try {
      const target = new URL(resolvedEntryPath, window.location.origin);
      target.searchParams.set("tourBooked", "1");
      if (referralLeadId) {
        target.searchParams.set("bookedLeadId", String(referralLeadId));
      }
      if (res?.data?.addBooking?.time) {
        target.searchParams.set("bookedTourTime", String(res.data.addBooking.time));
      }
      if (bookingTypedata?.business?.name) {
        target.searchParams.set("bookedTourBusiness", String(bookingTypedata.business.name));
      }
      return `${target.pathname}${target.search}`;
    } catch (_err) {
      return resolvedEntryPath;
    }
  })();
  const encodedBusinessUrl = encodeURIComponent(String(businessUrl || ""));
  const encodedMeetingTitle = encodeURIComponent(String(meetingTitle || ""));
  const encodedTimeRange = encodeURIComponent(String(timeRange || ""));
  const encodedBookingTime = encodeURIComponent(String(res?.data?.addBooking?.time || ""));
  const safeColor = String(bookingTypedata?.color || "").trim().replace(/^#/, "hex-");
  const encodedColor = encodeURIComponent(safeColor);
  const encodedStaffUrl = encodeURIComponent(String(staffUrl || ""));
  const encodedUserName = encodeURIComponent(
    String(staffData?.staffByUrl?.user?.username || "")
  );
  const confirmationParams = new URLSearchParams({ entry: successEntryPath, timezone: storedTimeZone, calendar: getBookingDisplayCalendar(bookingTypedata.calendarSystem), clock: getBookingDisplayClock(bookingTypedata.calendarSystem) });
  const bookingUrl = `/${encodedBusinessUrl}/confirmed/${encodedMeetingTitle}/${encodedTimeRange}/${encodedBookingTime}/${encodedColor}/${encodedStaffUrl}/${encodedUserName}?${confirmationParams}`;
  const finishNavigation = () => {
    try {
      const successMeta = {
        bookedLeadId: referralLeadId ? String(referralLeadId) : "",
        bookedTourTime: res?.data?.addBooking?.time
          ? String(res.data.addBooking.time)
          : "",
        bookedTourBusiness:
          String(bookingTypedata?.business?.name || businessUrl || "").trim(),
        savedAt: new Date().toISOString(),
      };
      sessionStorage.setItem("referrerTourBookingSuccess", JSON.stringify(successMeta));
    } catch (_err) {
      // ignore session persistence errors
    }
    try {
      if (typeof window !== "undefined" && window.parent && window.parent !== window) {
        window.parent.postMessage(
          {
            type: "plany-booking-complete",
            bookingUrl,
          },
          window.location.origin
        );
        return;
      }
    } catch (_err) {
      // Fall through to local navigation.
    }
    navigate(bookingUrl, { replace: true });
  };


  // Confirmation delivery is owned by the server and retried independently.
  finishNavigation();
}


function BookingPage(props) {
  const { providerUser } = useContext(UserContext);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [answers, setAnswers] = useState({});
  const [addBooking, { loading: bookingSaving }] = useMutation(ADD_BOOKINGS);
  const [createCheckout, { loading: checkoutSaving }] = useMutation(CREATE_APPOINTMENT_CHECKOUT);
  const bookingRequestId = useRef(uuidv4());

  const [selectedOption, setSelectedOption] = useState("payNow");
  const [errors, setErrors] = useState({});
  const [hasError, setHasError] = useState(false);
  const [email, setEmail] = useState("");
  const [emailVerification, setEmailVerification] = useState(null);
  const bookerEmail = String(email || providerUser?.email || '').trim().toLowerCase();
  const bookerEmailRef = useRef(bookerEmail);
  if (bookerEmailRef.current !== bookerEmail) {
    bookerEmailRef.current = bookerEmail;
    if (emailVerification) setEmailVerification(null);
  }
  const emailProof = emailVerification?.email === bookerEmail ? emailVerification.token : '';
  const [name, setName] = useState("");
  const [reminderMinutes, setReminderMinutes] = useState(15);
  const [bookingForMode, setBookingForMode] = useState("self");
  const [bookedForName, setBookedForName] = useState("");
  const [bookedForEmail, setBookedForEmail] = useState("");
  const [bookedForPhone, setBookedForPhone] = useState("");
  const [additionalConfirmationEmails, setAdditionalConfirmationEmails] = useState("");
  const [sendCopyToBookedBy, setSendCopyToBookedBy] = useState(true);
  const [smsConsent, setSmsConsent] = useState(false);
  // NEW STATE FOR BOOKING ERROR
  const [bookingError, setBookingError] = useState(null);
  const parsedAdditionalConfirmationEmails = useMemo(
    () =>
      String(additionalConfirmationEmails || "")
        .split(/[;,]/)
        .map((item) => item.trim())
        .filter(Boolean),
    [additionalConfirmationEmails]
  );

  const getEphemeralValue = (key) => {
    try {
      const sessionValue = sessionStorage.getItem(key);
      if (sessionValue !== null && sessionValue !== undefined) return sessionValue;
      const legacyValue = localStorage.getItem(key);
      if (legacyValue !== null && legacyValue !== undefined) {
        sessionStorage.setItem(key, legacyValue);
        localStorage.removeItem(key);
        return legacyValue;
      }
    } catch (_err) {
      // ignore
    }
    return null;
  };

  const setEphemeralValue = React.useCallback((key, value) => {
    try {
      sessionStorage.setItem(key, value);
      return true;
    } catch (_err) {
      try {
        localStorage.setItem(key, value);
        return true;
      } catch (_err2) {
        return false;
      }
    }
  }, []);

  const removeEphemeralValue = React.useCallback((key) => {
    try {
      sessionStorage.removeItem(key);
    } catch (_err) {
      // ignore
    }
    try {
      localStorage.removeItem(key);
    } catch (_err) {
      // ignore
    }
  }, []);

  const { bookingTypeLink, staffUrl, businessUrl, time } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const storedEntryPath = React.useMemo(
    () => getEphemeralValue("bookingEntry") || "",
    []
  );
  const returnEntryPath = React.useMemo(
    () =>
      new URLSearchParams(location.search || "").get("entry") ||
      storedEntryPath ||
      "",
    [location.search, storedEntryPath]
  );
  const prefilledParams = React.useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    return {
      referralLeadId: String(params.get("referralLeadId") || "").trim(),
      coordinatorEmail: String(params.get("coordinatorEmail") || "").trim(),
      coordinatorName: String(params.get("coordinatorName") || "").trim(),
      email: String(params.get("email") || "").trim(),
      name: String(params.get("name") || "").trim(),
      phone: String(params.get("phone") || "").trim(),
      notes: String(params.get("notes") || "").trim(),
    };
  }, [location.search]);
  const storedDetailsRaw = getEphemeralValue("bookingDetails");
  const storedDetails = storedDetailsRaw ? JSON.parse(storedDetailsRaw) : null;
  const storedTimeZone = getBookingTimezone(resolveTimeZone(
    getEphemeralValue("timezone"),
    getBrowserTimeZone(),
    providerUser?.timeZone?.timezone
  ));
  const storedPrefillRaw = getEphemeralValue("bookingPrefill");
  const storedPrefill = storedPrefillRaw ? JSON.parse(storedPrefillRaw) : null;
  const effectivePrefill = React.useMemo(
    () => ({
      referralLeadId: prefilledParams.referralLeadId || storedPrefill?.referralLeadId || "",
      coordinatorEmail: prefilledParams.coordinatorEmail || storedPrefill?.coordinatorEmail || "",
      coordinatorName: prefilledParams.coordinatorName || storedPrefill?.coordinatorName || "",
      email: prefilledParams.email || storedPrefill?.email || "",
      name: prefilledParams.name || storedPrefill?.name || "",
      phone: prefilledParams.phone || storedPrefill?.phone || "",
      notes: prefilledParams.notes || storedPrefill?.notes || "",
    }),
    [prefilledParams, storedPrefill]
  );
  const shouldDefaultOnBehalf = React.useMemo(() => {
    const currentEmail = String(providerUser?.email || "").trim().toLowerCase();
    const prefillEmail = String(effectivePrefill.email || "").trim().toLowerCase();
    return !!currentEmail && !!prefillEmail && currentEmail !== prefillEmail;
  }, [effectivePrefill.email, providerUser?.email]);

  const parsedTime = parse(time, "yyyy-MM-dd'T'h:mma", new Date());
  const parsedYear = Number.isNaN(parsedTime.getTime()) ? new Date().getFullYear() : parsedTime.getFullYear();
  const parentPath = `/${businessUrl}/${bookingTypeLink}`;

  useEffect(() => {
    if (!businessUrl || !bookingTypeLink || !staffUrl || !time) {
      navigate(parentPath, { replace: true });
      return;
    }

    const routeBookingDetails = {
      businessUrl,
      bookingTypeLink,
      staffUrl,
      time,
    };

    const matchesRoute =
      storedDetails &&
      storedDetails.businessUrl === businessUrl &&
      storedDetails.bookingTypeLink === bookingTypeLink &&
      storedDetails.staffUrl === staffUrl &&
      storedDetails.time === time;

    if (!matchesRoute) {
      setEphemeralValue("bookingDetails", JSON.stringify(routeBookingDetails));
    }
    if (
      effectivePrefill.referralLeadId ||
      effectivePrefill.coordinatorEmail ||
      effectivePrefill.email ||
      effectivePrefill.name ||
      effectivePrefill.phone ||
      effectivePrefill.notes
    ) {
      setEphemeralValue("bookingPrefill", JSON.stringify(effectivePrefill));
    }

    const fallbackTimeZone = resolveTimeZone(
      storedTimeZone,
      getBrowserTimeZone(),
      providerUser?.timeZone?.timezone
    );

    if (!storedTimeZone) {
      setEphemeralValue("timezone", fallbackTimeZone);
    }
    if (returnEntryPath) {
      setEphemeralValue("bookingEntry", returnEntryPath);
    }

    const currentTime = utcToZonedTime(new Date(), fallbackTimeZone);
    if (parsedTime <= currentTime) {
      navigate(parentPath, { replace: true });
    }
  }, [
    businessUrl,
    bookingTypeLink,
    staffUrl,
    time,
    parsedTime,
    storedTimeZone,
    providerUser,
    navigate,
    storedDetails,
    parentPath,
    effectivePrefill,
    returnEntryPath,
    setEphemeralValue,
  ]);

  useEffect(() => {
    if (providerUser && providerUser.email) {
      setEmail(providerUser.email);
      return;
    }
    if (effectivePrefill.email) {
      setEmail(effectivePrefill.email);
      return;
    }
  }, [effectivePrefill.email, providerUser]);

  useEffect(() => {
    if (providerUser?.username) {
      setName(providerUser.username);
      return;
    }
    if (effectivePrefill.name) {
      setName(effectivePrefill.name);
    }
  }, [effectivePrefill.name, providerUser?.username]);

  useEffect(() => {
    if (!shouldDefaultOnBehalf) return;
    setBookingForMode("other");
    setBookedForName(String(effectivePrefill.name || "").trim());
    setBookedForEmail(String(effectivePrefill.email || "").trim());
    setBookedForPhone(String(effectivePrefill.phone || "").trim());
  }, [
    effectivePrefill.email,
    effectivePrefill.name,
    effectivePrefill.phone,
    shouldDefaultOnBehalf,
  ]);

  const { loading: staffLoading, error: staffError, data: staffData } = useQuery(GET_STAFF_BY_URL, {
    variables: { staffUrl, businessUrl },
  });
  const { loading: businessBookingTypeLoading, error: businessBookingTypeError, data: businessBookingTypes } = useQuery(
    GET_BUSINESS_BOOKING_TYPES,
    { variables: { businessUrl } }
  );
  const bookingTypedata = businessBookingTypes?.getBusinessBookingTypes?.find((obj) => obj.link === bookingTypeLink);
  const skipOtherQueries = bookingTypedata?.id ? false : true;
  const { loading: questionLoading, error: questionError, data: questionData } = useQuery(GET_INVITEE_QUESTIONS, {
    variables: { bookingTypeId: parseInt(bookingTypedata?.id) },
    skip: skipOtherQueries,
  });
  const { loading: answerLoading, error: answerError, data: answerData } = useQuery(GET_INVITEE_ANSWERS, {
    variables: { bookingTypeId: parseInt(bookingTypedata?.id) },
    skip: skipOtherQueries,
  });
  const { data: myBookingsData } = useQuery(GET_BOOKINGS_BY_EMAIL, {
    variables: {
      email: providerUser?.email || "",
      selectedYear: parsedYear,
    },
    skip: !providerUser?.email,
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
  });

  const slotUtcIso = useMemo(() => {
    if (!storedTimeZone || Number.isNaN(parsedTime.getTime())) return null;
    return zonedTimeToUtc(parsedTime, storedTimeZone).toISOString();
  }, [parsedTime, storedTimeZone]);
  const selectedAssignedStaff = useMemo(
    () => bookingTypedata?.assignedStaffs?.find((val) => val.url === staffUrl),
    [bookingTypedata, staffUrl]
  );
  const selectedStaffForDisplay = useMemo(() => {
    const staffByUrl = staffData?.staffByUrl;
    if (!selectedAssignedStaff) return staffByUrl || null;
    if (!staffByUrl) return selectedAssignedStaff;
    if (Number(staffByUrl?.id) !== Number(selectedAssignedStaff?.staffId)) {
      return {
        ...selectedAssignedStaff,
        image: selectedAssignedStaff?.image || staffByUrl?.image,
        user: selectedAssignedStaff?.user || staffByUrl?.user,
      };
    }
    return {
      ...selectedAssignedStaff,
      image: selectedAssignedStaff?.image || staffByUrl?.image,
      user: selectedAssignedStaff?.user || staffByUrl?.user,
    };
  }, [selectedAssignedStaff, staffData]);

  const alreadyBookedThisSlot = useMemo(() => {
    if (!providerUser?.email || !bookingTypedata?.id || !slotUtcIso) return false;
    const slotTimeMs = new Date(slotUtcIso).getTime();
    if (Number.isNaN(slotTimeMs)) return false;
    const bookings = myBookingsData?.getBookingsByEmail || [];
    return bookings.some((booking) => {
      if (Number(booking.status) === Number(BookingTypeStatus.DELETED)) return false;
      if (Number(booking.bookingTypeId) !== Number(bookingTypedata.id)) return false;
      const bookingTimeMs = new Date(booking.time).getTime();
      return bookingTimeMs === slotTimeMs;
    });
  }, [providerUser, bookingTypedata, slotUtcIso, myBookingsData]);

  const disableBookingActions = hasError || alreadyBookedThisSlot;
  const disableReason = alreadyBookedThisSlot
    ? "You already scheduled this exact slot. Choose a different time."
    : hasError
    ? "Please complete required fields before scheduling."
    : "";

  const validateQuestion = (questionId, answerValue) => {
    const question = questionData.getInviteeQuestions.find((q) => q.id === questionId);
    const title = String(question?.title || "").toLowerCase();
    const firstAnswerRaw = Array.isArray(answerValue) ? answerValue[0] : "";
    const firstAnswer = typeof firstAnswerRaw === "string" ? firstAnswerRaw.trim() : String(firstAnswerRaw || "").trim();

    const effectiveAnswer =
      firstAnswer ||
      (title.includes("email") ? String(providerUser?.email || "").trim() : "") ||
      (title.includes("name") ? String(providerUser?.username || "").trim() : "");

    const emailAnswer = Object.values(answers).find((_, qId) =>
      questionData.getInviteeQuestions[qId]?.title.toLowerCase() === "email"
    );
    const phoneAnswer = Object.values(answers).find((_, qId) =>
      questionData.getInviteeQuestions[qId]?.title.toLowerCase() === "phone number"
    );

    const isEmailQ = question.title.toLowerCase() === "email";
    const isPhoneQ = question.title.toLowerCase() === "phone number";

    const hasEmail = emailAnswer && emailAnswer[0]?.trim();
    const hasPhone = phoneAnswer && phoneAnswer[0]?.trim();

    // If it's email or phone, then we allow skipping one of them if the other is filled
    if ((isEmailQ || isPhoneQ) && (hasEmail || hasPhone)) {
      return false;
    }

    // If it's a required field, validate as usual
    if (question.required && !effectiveAnswer) {
      return true;
    }

    // Validate email format if email is entered
    if (isEmailQ && effectiveAnswer) {
      return !validateEmail(effectiveAnswer);
    }

    return false;
  };

  // Revalidate answers whenever questions or answers change
  useEffect(() => {
    if (questionData?.getInviteeQuestions) {
      const updatedErrors = {};
      questionData.getInviteeQuestions.forEach((question) => {
        const ans = answers[question.id] !== undefined ? answers[question.id] : [""];
        updatedErrors[question.id] = validateQuestion(question.id, ans);
      });
      setErrors(updatedErrors);
    }
  }, [questionData, answers]);


  useEffect(() => {
    if (questionData?.getInviteeQuestions) {
      const defaults = {};
      questionData.getInviteeQuestions.forEach((question) => {
        const title = String(question.title || "").toLowerCase();
        if (!answers[question.id] || answers[question.id][0].trim() === "") {
          if (title === "email" && (effectivePrefill.email || providerUser?.email)) {
            defaults[question.id] = [effectivePrefill.email || providerUser.email];
          }
          if (title === "name" && (effectivePrefill.name || providerUser?.username)) {
            defaults[question.id] = [effectivePrefill.name || providerUser.username];
          }
          if (title === "phone number" && effectivePrefill.phone) {
            defaults[question.id] = [effectivePrefill.phone];
          }
          if (title === "notes" && effectivePrefill.notes) {
            defaults[question.id] = [effectivePrefill.notes];
          }
        }
      });
      if (Object.keys(defaults).length > 0) {
        setAnswers((prev) => ({ ...prev, ...defaults }));
      }
    }
  }, [answers, effectivePrefill, providerUser, questionData]);


  useEffect(() => {
    setHasError(Object.values(errors).some((error) => error));
  }, [errors]);

  useEffect(() => {
    const savedAnswers = getEphemeralValue("bookingAnswers");
    const savedBookingEmail = getEphemeralValue("bookingEmail");
    if (savedAnswers) {
      setAnswers(JSON.parse(savedAnswers));
    }
    if (savedBookingEmail) {
      setEmail(JSON.parse(savedBookingEmail));
    }
  }, []);

  // Persist answers and bookingEmail to session storage while flow is active.
  useEffect(() => {
    setEphemeralValue("bookingAnswers", JSON.stringify(answers));
    setEphemeralValue("bookingEmail", JSON.stringify(email));
  }, [answers, email, setEphemeralValue]);


  if (staffLoading || businessBookingTypeLoading || questionLoading || answerLoading) return "Loading";
  if (staffError || businessBookingTypeError || questionError || answerError) return <Error />;

  const handleAnswerChange = (questionId, value, isCheckbox = false) => {
    setAnswers((prev) => {
      let newAnswer;
      if (isCheckbox) {
        const current = prev[questionId] || [];
        newAnswer = current.includes(value)
          ? current.filter((v) => v !== value)
          : [...current, value];
      } else {
        newAnswer = [value];
      }
      const question = questionData.getInviteeQuestions.find((q) => q.id === questionId);
      if (question && question.title.toLowerCase() === "email") {
        setEmail(value);
      }
      if (question && question.title.toLowerCase() === "name") {
        setName(value);
      }
      return { ...prev, [questionId]: newAnswer };
    });
  };

  const handlePaymentOptionChange = (event) => {
    setSelectedOption(event.target.value);
  };

  const handleReminderChange = (event) => {
    setReminderMinutes(Number(event.target.value));
  };

  // onConfirm callback
  const handleConfirmation = (answersRef, emailRef, paymentProvider = "STRIPE") => {
    if (!emailProof) { setBookingError('Verify your email to continue.'); return; }
    const localAnswers = answersRef || answers || {};
    if (Object.keys(localAnswers).length === 0 && !emailRef) {
      console.warn("No answers provided; skipping booking.");
      return;
    }
    if (
      bookingForMode === "other" &&
      !String(bookedForName || "").trim() &&
      !String(bookedForEmail || "").trim() &&
      !String(bookedForPhone || "").trim()
    ) {
      setBookingError("Please add at least one attendee contact detail when booking on behalf of someone else.");
      return;
    }
    if (!smsConsent) {
      setBookingError("Please agree to the SMS consent before booking.");
      return;
    }
    // Reset any previous booking error before attempting booking
    setBookingError(null);

    const payOnline = selectedOption === "payNow" && Number(bookingTypedata.price) > 0;
    const submitBooking = payOnline ? createCheckout : addBooking;
    submitBooking({
      variables: {
        ...(payOnline ? { provider: paymentProvider } : {}),
        input: {
          emailVerificationToken: emailProof,
          bookingTypeId: parseInt(bookingTypedata.id),
          timezone: storedTimeZone,
          bookingId: bookingRequestId.current,
          time: zonedTimeToUtc(parsedTime, storedTimeZone).toISOString(),
          answers: Object.keys(localAnswers).map(key => ({
            questionId: parseInt(key, 10),
            answer: localAnswers[key][0],
          })),
          staffUrl,
          businessUrl,
          ...(effectivePrefill.referralLeadId
            ? { referralLeadId: effectivePrefill.referralLeadId }
            : {}),
          ...(effectivePrefill.coordinatorEmail
            ? { coordinatorEmail: effectivePrefill.coordinatorEmail }
            : {}),
          ...(effectivePrefill.coordinatorName
            ? { coordinatorName: effectivePrefill.coordinatorName }
            : {}),
          bookedByRole:
            bookingForMode === "other"
              ? providerUser?.isAgent
                ? "AGENT"
                : providerUser?.userId
                  ? "USER"
                  : "BOOKER"
              : "SELF",
          bookedByName: name || providerUser?.username || "",
          bookedByEmail: email || providerUser?.email || "",
          bookedForName:
            bookingForMode === "other" ? bookedForName : name || providerUser?.username || "",
          bookedForEmail:
            bookingForMode === "other" ? bookedForEmail : email || providerUser?.email || "",
          bookedForPhone: bookingForMode === "other" ? bookedForPhone : effectivePrefill.phone || "",
          additionalConfirmationEmails: parsedAdditionalConfirmationEmails,
          sendCopyToBookedBy,
          finalPrice:
            selectedOption === "payNow"
              ? bookingTypedata.price - bookingTypedata.price * (bookingTypedata.discount / 100)
              : 0,
          reminderMinutes: reminderMinutes,
        },
      },
    })
      .then((res) => {
        if (payOnline) {
          rememberCheckoutDisplay(getBookingDisplayCalendar(bookingTypedata.calendarSystem), getBookingDisplayClock(bookingTypedata.calendarSystem));
          window.location.assign(res.data.createAppointmentCheckout.checkoutUrl);
          return;
        }
        // Clear transient form data after successful booking creation.
        removeEphemeralValue("bookingAnswers");
        removeEphemeralValue("bookingEmail");
        removeEphemeralValue("bookingEntry");
        navigateToBookingConfirmation({
          time,
          bookingTypedata,
          storedTimeZone,
          staffData,
          email: emailRef ?? email,
          name,
          businessUrl,
          staffUrl,

          navigate,
          res,
          providerUser,
          entryPath: returnEntryPath,
          referralLeadId: effectivePrefill.referralLeadId,
          bookedForName:
            bookingForMode === "other" ? bookedForName : name || providerUser?.username || "",
          bookedForEmail:
            bookingForMode === "other" ? bookedForEmail : email || providerUser?.email || "",
          additionalConfirmationEmails,
          sendCopyToBookedBy,
        });
      })
      .catch((err) => {
        if (/verify your email/i.test(err.message)) setEmailVerification(null);
        console.error("Booking creation error:", err);
        // Update state to display error message to user
        setBookingError(err.message);
      });
  };

  return (
    <Dialog
      open
      fullWidth
      maxWidth="lg"
      fullScreen={isSmallScreen}
      scroll="paper"
      onClose={() => navigate(parentPath, { replace: true })}
      PaperProps={{
        sx: {
          borderRadius: isSmallScreen ? 0 : 3.5,
          overflow: "hidden",
          width: isSmallScreen ? "100%" : undefined,
          maxWidth: isSmallScreen ? "100%" : undefined,
          height: isSmallScreen ? "100%" : "min(94vh, 1100px)",
          maxHeight: isSmallScreen ? "100%" : "94vh",
          m: isSmallScreen ? 0 : 2,
          bgcolor: "background.default",
          backgroundImage: "none",
          color: "text.primary",
          colorScheme: theme.palette.mode,
          boxShadow: "0 32px 80px rgba(15,31,46,0.18)",
        },
      }}
    >
      <DialogContent
        sx={{
          p: { xs: 2, sm: 2.5, md: 3 },
          bgcolor: "background.default",
          color: "text.primary",
          overflowY: "auto",
          height: isSmallScreen ? "100%" : "auto",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            mb: { xs: 1, sm: 1.25 },
          }}
        >
          <IconButton
            onClick={() => navigate(parentPath, { replace: true })}
            sx={{
              width: 40,
              height: 40,
              border: 1, borderColor: "divider",
              backgroundColor: "background.paper",
              color: "text.secondary",
              "&:hover": {
                backgroundColor: "background.paper",
                borderColor: "divider",
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Card
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            alignItems: "flex-start",
            width: "100%",
            height: "auto",
            boxShadow: "none",
            gap: { xs: 2, lg: 2.5 },
            background: "transparent",
            backgroundImage: "none",
            minHeight: isSmallScreen ? "calc(100vh - 32px)" : "auto",
          }}
        >
          <Box
            sx={{
              flex: { xs: "1 1 100%", lg: "0 0 72%" },
              minWidth: 0,
              display: "flex",
              flexDirection: { xs: "column", lg: "row" },
              alignItems: "stretch",
              borderRadius: 3,
              border: 1, borderColor: "divider",
              bgcolor: "background.paper",
              color: "text.primary",
            }}
          >
            <BookingHeader
              bookingData={bookingTypedata}
              parsedTime={parsedTime}
              staff={selectedStaffForDisplay}
              storedTimeZone={storedTimeZone}
              navigate={navigate}
            />
            <Divider
              orientation="vertical"
              flexItem
              sx={{
                display: { xs: "none", lg: "block" },
                borderColor: "divider",
              }}
            />
            <Divider
              sx={{
                display: { xs: "block", lg: "none" },
                borderColor: "divider",
              }}
            />
            <BookingForm
              questions={questionData.getInviteeQuestions}
              answers={answers}
              errors={errors}
              onAnswerChange={handleAnswerChange}
              answerData={answerData}
              staff={selectedStaffForDisplay}
              bookingForMode={bookingForMode}
              onBookingForModeChange={setBookingForMode}
              bookedForName={bookedForName}
              onBookedForNameChange={setBookedForName}
              bookedForEmail={bookedForEmail}
              onBookedForEmailChange={setBookedForEmail}
              bookedForPhone={bookedForPhone}
              onBookedForPhoneChange={setBookedForPhone}
              additionalConfirmationEmails={additionalConfirmationEmails}
              onAdditionalConfirmationEmailsChange={setAdditionalConfirmationEmails}
              sendCopyToBookedBy={sendCopyToBookedBy}
              onSendCopyToBookedByChange={setSendCopyToBookedBy}
              smsConsent={smsConsent}
              onSmsConsentChange={setSmsConsent}
              smsConsentError={bookingError?.toLowerCase?.().includes("sms consent") ? bookingError : ""}
            />
          </Box>
          <Box
            sx={{
              flex: { xs: "1 1 100%", lg: "0 0 26%" },
              minWidth: { xs: 0, lg: 260 },
              maxWidth: { xs: "100%", lg: 320 },
              padding: { xs: 0, md: 0 },
              mt: { xs: 0, lg: 1.5 },
              width: "100%",
            }}
          >
            <FormControl fullWidth sx={{ marginBottom: "1rem" }}>
              <InputLabel
                id="reminder-select-label"
                shrink
                sx={{
                  color: "text.secondary",
                  fontWeight: 600,
                  "&.Mui-focused": {
                    color: "primary.main",
                  },
                }}
              >
                Reminder Time
              </InputLabel>
              <Select
                labelId="reminder-select-label"
                id="reminder-select"
                value={reminderMinutes}
                label="Reminder Time"
                onChange={handleReminderChange}
                size="small"
                sx={{
                  borderRadius: 2.5,
                  backgroundColor: "background.paper",
                  boxShadow: "0 12px 28px rgba(15,31,46,0.06)",
                  minHeight: 48,
                  "& .MuiSelect-select": {
                    pt: 1.8,
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "divider",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "divider",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "primary.main",
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      bgcolor: "background.paper",
                      backgroundImage: "none",
                      color: "text.primary",
                      mt: 0.75,
                      borderRadius: 2.5,
                      border: 1, borderColor: "divider",
                      boxShadow: "0 18px 36px rgba(15,31,46,0.12)",
                    },
                  },
                }}
              >
                <MenuItem value={5}>5 minutes</MenuItem>
                <MenuItem value={10}>10 minutes</MenuItem>
                <MenuItem value={15}>15 minutes</MenuItem>
                <MenuItem value={30}>30 minutes</MenuItem>
                <MenuItem value={60}>1 hour</MenuItem>
                <MenuItem value={120}>2 hours</MenuItem>
                <MenuItem value={1440}>1 day</MenuItem>
              </Select>
            </FormControl>
            <BookingEmailVerification key={bookerEmail} email={bookerEmail} proof={emailProof} onVerified={setEmailVerification} disabled={bookingSaving || checkoutSaving} />
            <PaymentSection
              businessUrl={businessUrl}
              selectedOption={selectedOption}
              onOptionChange={handlePaymentOptionChange}
              bookingTypedata={{ ...bookingTypedata }}
              hasError={disableBookingActions || bookingSaving || checkoutSaving || !emailProof}
              disableReason={disableReason || (!emailProof ? 'Verify your email to continue.' : '')}
              onConfirm={handleConfirmation}
              email={email}
              answers={answers}
            />
            {bookingError && (
              <Typography variant="body2" color="error" align="center" sx={{ marginTop: "1rem" }}>
                {bookingError}
              </Typography>
            )}
          </Box>
        </Card>
      </DialogContent>
    </Dialog>
  );
}

export default BookingPage;
